import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { API, axios } from "@/api";
import { BottomNav } from "@/components/shared/Navigation";
import {
  ArrowLeft, Search, MapPin, CheckCircle, AlertTriangle,
  Gift, Loader2, Send, ChevronRight, X, LocateFixed,
  Navigation, ChevronDown, Tag, DollarSign, Percent, Star,
  ShoppingBag, Coffee, Sparkles, HelpCircle,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { trackPageView, trackError } from "@/lib/track";
import { trackContributionSubmitted } from "@/lib/events";
import { useCopyBank } from "@/hooks/useCopyBank";

/* ═══════ Offer Type Configuration ═══════ */
const OFFER_TYPES = [
  {
    value: "free_item",
    label: "Free Item",
    icon: Gift,
    color: "text-emerald-600 bg-emerald-50",
    fields: [
      { key: "item_name", label: "What's the free item?", placeholder: "e.g., Free entrée, free dessert", type: "text" },
      { key: "est_value", label: "Est. value ($)", placeholder: "e.g., 15", type: "number" },
    ],
    buildDescription: (f) => {
      const item = (f.item_name || "item").trim();
      const prefix = item.toLowerCase().startsWith("free") ? "" : "Free ";
      return `${prefix}${item}${f.est_value ? ` (~$${f.est_value})` : ""}`;
    },
  },
  {
    value: "bogo",
    label: "BOGO (Buy One Get One)",
    icon: ShoppingBag,
    color: "text-sky-600 bg-sky-50",
    fields: [
      { key: "est_value", label: "Est. value of the free item ($)", placeholder: "e.g., 12", type: "number" },
    ],
    buildDescription: (f) => `Buy one, get one free${f.est_value ? ` (≈$${f.est_value} value)` : ""}`,
  },
  {
    value: "percent_off",
    label: "Percentage Off",
    icon: Percent,
    color: "text-violet-600 bg-violet-50",
    fields: [
      { key: "percent_amount", label: "Percentage off (%)", placeholder: "e.g., 25", type: "number" },
    ],
    buildDescription: (f) => `${f.percent_amount || "?"}% off`,
  },
  {
    value: "dollar_off",
    label: "Dollar Amount Off",
    icon: DollarSign,
    color: "text-amber-600 bg-amber-50",
    fields: [
      { key: "dollar_amount", label: "Dollar amount off ($)", placeholder: "e.g., 10", type: "number" },
    ],
    buildDescription: (f) => `$${f.dollar_amount || "?"} off`,
  },
  {
    value: "free_drink",
    label: "Free Drink",
    icon: Coffee,
    color: "text-teal-600 bg-teal-50",
    fields: [
      { key: "est_value", label: "Est. value ($)", placeholder: "e.g., 6", type: "number" },
    ],
    buildDescription: (f) => `Free drink${f.est_value ? ` (~$${f.est_value})` : ""}`,
  },
  {
    value: "points_multiplier",
    label: "Points / Rewards Multiplier",
    icon: Star,
    color: "text-orange-600 bg-orange-50",
    fields: [
      { key: "points_detail", label: "Points or multiplier details", placeholder: "e.g., Double points, 500 bonus points", type: "text" },
    ],
    buildDescription: (f) => f.points_detail || "Points/rewards multiplier",
  },
  {
    value: "gift_surprise",
    label: "Gift / Birthday Surprise",
    icon: Sparkles,
    color: "text-pink-600 bg-pink-50",
    fields: [
      { key: "est_value", label: "Est. value ($) — if known", placeholder: "e.g., 10", type: "number" },
    ],
    buildDescription: (f) => `Birthday surprise gift${f.est_value ? ` (≈$${f.est_value})` : ""}`,
  },
  {
    value: "free_service",
    label: "Free Service",
    icon: Tag,
    color: "text-indigo-600 bg-indigo-50",
    fields: [
      { key: "service_name", label: "What's the free service?", placeholder: "e.g., Free facial, free car wash", type: "text" },
      { key: "est_value", label: "Est. value ($)", placeholder: "e.g., 40", type: "number" },
    ],
    buildDescription: (f) => `Free ${f.service_name || "service"}${f.est_value ? ` (~$${f.est_value})` : ""}`,
  },
  {
    value: "other",
    label: "Other",
    icon: HelpCircle,
    color: "text-slate-600 bg-slate-100",
    fields: [
      { key: "custom_description", label: "Describe the offer", placeholder: "e.g., Free birthday upgrade with meal purchase", type: "text" },
      { key: "est_value", label: "Est. value ($)", placeholder: "e.g., 10", type: "number" },
    ],
    buildDescription: (f) => f.custom_description || "Birthday offer",
  },
];

const CATEGORIES = [
  "Restaurant", "Fast Food", "Coffee", "Bakery / Dessert",
  "Cosmetics", "Clothing", "Shoes", "Accessories / Jewelry",
  "Entertainment", "Fitness", "Ice Cream / Frozen Yogurt", "Other",
];

/* ═══════ Sub-Components ═══════ */

const MatchCard = ({ match, selected, onSelect, compact }) => (
  <motion.button
    initial={{ opacity: 0, y: 8 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: -8, height: 0, marginBottom: 0, padding: 0, overflow: "hidden" }}
    transition={{ duration: 0.2 }}
    layout
    onClick={() => onSelect(match)}
    className={`w-full text-left rounded-xl border p-3 transition-all ${
      selected?.business_id === match.business_id
        ? "border-emerald-400 bg-emerald-50 ring-2 ring-emerald-200"
        : "border-slate-200 bg-white hover:border-slate-300"
    }`}
    data-testid={`match-option-${match.business_id}`}
  >
    <div className="flex items-center gap-2.5">
      <div className="w-9 h-9 rounded-lg overflow-hidden flex-shrink-0 bg-slate-50 border border-slate-100 flex items-center justify-center">
        {match.logo_url ? (
          <img src={match.logo_url} alt="" className="w-full h-full object-contain" />
        ) : (
          <Gift className="w-4 h-4 text-slate-400" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-slate-800">{match.name}</p>
        <p className="text-[10px] text-slate-400">{match.category}{match.confidence >= 90 ? " · Best match" : ""}</p>
      </div>
      {selected?.business_id === match.business_id && (
        <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0" />
      )}
    </div>
    {!compact && match.offer_title && (
      <p className="text-xs text-slate-500 mt-1.5 line-clamp-1">{match.offer_title}</p>
    )}
    {!compact && match.addresses?.length > 0 && (
      <div className="mt-1.5 space-y-0.5">
        {match.addresses.slice(0, 2).map((addr, i) => (
          <div key={i} className="flex items-center gap-1 text-[10px] text-slate-400">
            <MapPin className="w-3 h-3 flex-shrink-0" />
            <span className="truncate">{addr}</span>
          </div>
        ))}
      </div>
    )}
  </motion.button>
);

const LocationCard = ({ loc, selected, onSelect }) => (
  <motion.button
    initial={{ opacity: 0, y: 6 }}
    animate={{ opacity: 1, y: 0 }}
    onClick={() => onSelect(loc)}
    className={`w-full text-left rounded-xl border p-2.5 transition-all ${
      selected?.location_id === loc.location_id
        ? "border-teal-400 bg-teal-50 ring-2 ring-teal-200"
        : "border-slate-200 bg-white hover:border-slate-300"
    }`}
    data-testid={`location-option-${loc.location_id}`}
  >
    <div className="flex items-center gap-2">
      <MapPin className="w-4 h-4 text-teal-500 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-xs font-medium text-slate-700 truncate">{loc.address}</p>
        <div className="flex items-center gap-2 mt-0.5">
          {loc.distance_miles != null && (
            <span className="text-[10px] font-semibold text-teal-600">{loc.distance_miles} mi</span>
          )}
          {loc.participation_status === "verified" && (
            <span className="text-[10px] text-emerald-600 flex items-center gap-0.5">
              <CheckCircle className="w-2.5 h-2.5" /> Verified
            </span>
          )}
        </div>
      </div>
      {selected?.location_id === loc.location_id && (
        <CheckCircle className="w-4 h-4 text-teal-500 flex-shrink-0" />
      )}
    </div>
  </motion.button>
);

/* ═══════ Main Page ═══════ */

export default function SubmitOfferPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { getText } = useCopyBank();

  // Mode: "search" | "new" | "success"
  const [mode, setMode] = useState("search");
  const [searchQuery, setSearchQuery] = useState("");
  const [matches, setMatches] = useState([]);
  const [checking, setChecking] = useState(false);
  const [selectedMatch, setSelectedMatch] = useState(null);

  // GPS
  const [gpsCoords, setGpsCoords] = useState(null);
  const [gpsAddress, setGpsAddress] = useState("");
  const [gpsLoading, setGpsLoading] = useState(false);
  const [gpsRequested, setGpsRequested] = useState(false);

  // Location verification
  const [nearbyLocations, setNearbyLocations] = useState([]);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [nearbyCount, setNearbyCount] = useState(0);
  const [showAllLocations, setShowAllLocations] = useState(false);

  // Offer type
  const [offerType, setOfferType] = useState("");
  const [offerFields, setOfferFields] = useState({});

  // Form fields
  const [category, setCategory] = useState("");
  const [location, setLocation] = useState("");
  const [requiresApp, setRequiresApp] = useState(false);
  const [requiresPurchase, setRequiresPurchase] = useState(false);
  const [website, setWebsite] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // New business name (for "none of these" mode)
  const [newBusinessName, setNewBusinessName] = useState("");

  // GPS detection function
  const detectGPS = useCallback(() => {
    if (!navigator.geolocation) {
      toast.error("Geolocation not supported by your browser");
      return;
    }
    setGpsLoading(true);
    setGpsRequested(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const coords = { lat: pos.coords.latitude, lon: pos.coords.longitude };
        setGpsCoords(coords);
        try {
          const res = await fetch(
            `https://nominatim.openstreetmap.org/reverse?lat=${coords.lat}&lon=${coords.lon}&format=json&addressdetails=1`
          );
          const data = await res.json();
          const addr = data.address || {};
          const parts = [
            addr.house_number, addr.road || addr.pedestrian,
            addr.city || addr.town || addr.village,
            addr.state, addr.postcode,
          ].filter(Boolean);
          const fullAddr = parts.join(", ");
          setGpsAddress(fullAddr);
          if (!location) setLocation(fullAddr);
          toast.success("Location detected!");
        } catch (e) {
          toast.error("Could not determine address from GPS"); trackError("gps_detect", e);
        } finally {
          setGpsLoading(false);
        }
      },
      (err) => {
        setGpsLoading(false);
        if (err.code === 1) toast.error("Location access denied. Please allow location access in your browser settings.");
        else toast.error("Could not detect location. Please enter manually.");
      },
      { timeout: 10000, enableHighAccuracy: true }
    );
  }, [location]);

  // Auto-detect GPS on mount
  useEffect(() => {
    trackPageView("submit_offer");
    if (!gpsRequested) detectGPS();
  }, [detectGPS, gpsRequested]);

  // Debounced name check
  const checkName = useCallback(async (name) => {
    if (name.length < 2) { setMatches([]); return; }
    setChecking(true);
    try {
      const res = await axios.post(`${API}/submissions/check-name`, { name });
      setMatches(res.data.matches || []);
    } catch (e) { setMatches([]); trackError("submission_name_check", e); }
    finally { setChecking(false); }
  }, []);

  useEffect(() => {
    if (selectedMatch || mode !== "search") return;
    if (searchQuery.length < 2) { setMatches([]); return; }
    const timer = setTimeout(() => checkName(searchQuery), 400);
    return () => clearTimeout(timer);
  }, [searchQuery, selectedMatch, mode, checkName]);

  // When a match is selected
  const handleSelectMatch = async (match) => {
    setSelectedMatch(match);
    setCategory(match.category || "");
    setMatches([match]); // Keep only the selected one visible

    // Fetch nearby locations
    setLoadingLocations(true);
    try {
      const payload = {
        business_id: match.business_id,
        lat: gpsCoords?.lat || null,
        lon: gpsCoords?.lon || null,
        radius: user?.search_radius || 10,
      };
      const res = await axios.post(`${API}/submissions/nearby-locations`, payload);
      setNearbyLocations(res.data.locations || []);
      setNearbyCount(res.data.nearby_count || 0);
    } catch (e) {
      setNearbyLocations([]); trackError("nearby_locations", e);
    } finally {
      setLoadingLocations(false);
    }
  };

  // Deselect match → go back to search
  const deselectMatch = () => {
    setSelectedMatch(null);
    setSelectedLocation(null);
    setNearbyLocations([]);
    setOfferType("");
    setOfferFields({});
    setShowAllLocations(false);
    // Re-trigger search
    if (searchQuery.length >= 2) checkName(searchQuery);
  };

  // "None of these" → new business mode
  const goToNewMode = () => {
    setNewBusinessName(searchQuery);
    setMode("new");
    if (gpsAddress && !location) setLocation(gpsAddress);
  };

  // Build offer description from type + fields
  const getOfferConfig = () => OFFER_TYPES.find((t) => t.value === offerType);
  const buildDescription = () => {
    const cfg = getOfferConfig();
    return cfg ? cfg.buildDescription(offerFields) : "";
  };

  // Get dollar value for submission
  const getSubmitValue = () => {
    const f = offerFields;
    if (f.est_value) return f.est_value;
    if (f.dollar_amount) return f.dollar_amount;
    return "";
  };

  // Can submit?
  const canSubmit = offerType && (
    offerType === "gift_surprise" || // gift can be submitted without details
    Object.values(offerFields).some((v) => v && String(v).trim())
  );

  const handleSubmit = async () => {
    if (!canSubmit) { toast.error("Please fill in the offer details"); return; }
    setSubmitting(true);
    try {
      const businessName = mode === "new" ? newBusinessName : (selectedMatch?.name || searchQuery);
      await axios.post(`${API}/submissions`, {
        business_name: businessName,
        category,
        offer_type: offerType,
        offer_description: buildDescription(),
        offer_value: getSubmitValue(),
        offer_fields: offerFields,
        requires_app: requiresApp,
        requires_purchase: requiresPurchase,
        website,
        location,
        notes,
        matched_business_id: selectedMatch?.business_id || null,
        verified_location_id: selectedLocation?.location_id || null,
      });
      setMode("success");
      toast.success("Offer submitted for review!");
      // v1.0.0 GATE 3 — PostHog contribution_submitted
      trackContributionSubmitted(`offer_${offerType || "unknown"}`);
    } catch (e) {
      toast.error("Failed to submit"); trackError("offer_submit", e);
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setMode("search");
    setSearchQuery("");
    setSelectedMatch(null);
    setMatches([]);
    setSelectedLocation(null);
    setNearbyLocations([]);
    setOfferType("");
    setOfferFields({});
    setCategory("");
    setLocation(gpsAddress || "");
    setNotes("");
    setWebsite("");
    setRequiresApp(false);
    setRequiresPurchase(false);
    setNewBusinessName("");
    setShowAllLocations(false);
  };

  /* ═══════ Shared: Offer Type Section ═══════ */
  const renderOfferSection = () => {
    const cfg = getOfferConfig();
    return (
      <div className="space-y-3">
        {/* Offer Type Dropdown */}
        <div>
          <label className="text-xs font-semibold text-slate-600 block mb-1.5">What type of offer is it?</label>
          <div className="relative">
            <select
              value={offerType}
              onChange={(e) => { setOfferType(e.target.value); setOfferFields({}); }}
              className="w-full text-sm border border-slate-200 rounded-xl px-3 py-2.5 pr-8 focus:outline-none focus:ring-2 focus:ring-indigo-200 bg-white appearance-none"
              data-testid="offer-type-select"
            >
              <option value="">Select offer type...</option>
              {OFFER_TYPES.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
          </div>
        </div>

        {/* Dynamic Fields based on offer type */}
        <AnimatePresence mode="wait">
          {cfg && (
            <motion.div
              key={offerType}
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2 }}
              className="space-y-3 overflow-hidden"
            >
              {/* Type badge */}
              <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold ${cfg.color}`}>
                <cfg.icon className="w-3.5 h-3.5" />
                {cfg.label}
              </div>

              {cfg.fields.map((field) => (
                <div key={field.key}>
                  <label className="text-xs font-semibold text-slate-600 block mb-1">{field.label}</label>
                  {field.type === "number" ? (
                    <div className="relative">
                      {field.label.includes("%") ? (
                        <Percent className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      ) : (
                        <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      )}
                      <input
                        type="number"
                        value={offerFields[field.key] || ""}
                        onChange={(e) => setOfferFields({ ...offerFields, [field.key]: e.target.value })}
                        placeholder={field.placeholder}
                        className="w-full text-sm border border-slate-200 rounded-xl pl-9 pr-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-200"
                        data-testid={`offer-field-${field.key}`}
                      />
                    </div>
                  ) : (
                    <input
                      type="text"
                      value={offerFields[field.key] || ""}
                      onChange={(e) => setOfferFields({ ...offerFields, [field.key]: e.target.value })}
                      placeholder={field.placeholder}
                      className="w-full text-sm border border-slate-200 rounded-xl px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-200"
                      data-testid={`offer-field-${field.key}`}
                    />
                  )}
                </div>
              ))}

              {/* Preview of generated description */}
              {Object.values(offerFields).some(Boolean) && (
                <div className="bg-slate-50 rounded-lg p-2.5 border border-slate-100">
                  <p className="text-[10px] text-slate-400 font-medium mb-0.5">Preview</p>
                  <p className="text-xs font-semibold text-slate-700" data-testid="offer-preview">{buildDescription()}</p>
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  };

  /* ═══════ Shared: Location Section ═══════ */
  const renderLocationField = () => (
    <div>
      <div className="flex items-center justify-between mb-1">
        <label className="text-xs font-semibold text-slate-600">Location (full address)</label>
        <button
          onClick={detectGPS}
          disabled={gpsLoading}
          className="text-[10px] font-semibold text-teal-600 flex items-center gap-0.5 hover:text-teal-700 disabled:opacity-50"
          data-testid="detect-gps-btn"
        >
          {gpsLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <LocateFixed className="w-3 h-3" />}
          {gpsLoading ? "Detecting..." : gpsCoords ? "Re-detect" : "Detect Location"}
        </button>
      </div>
      <div className="relative">
        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <input
          type="text"
          value={location}
          onChange={(e) => setLocation(e.target.value)}
          placeholder="123 Main St, Sacramento, CA 95814"
          className="w-full text-sm border border-slate-200 rounded-xl pl-9 pr-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-200"
          data-testid="location-input"
        />
      </div>
      {selectedLocation && (
        <p className="text-[10px] text-teal-600 mt-1 flex items-center gap-1">
          <CheckCircle className="w-3 h-3" /> Verified from our database
        </p>
      )}
    </div>
  );

  /* ═══════ Shared: Extra fields + Submit ═══════ */
  const renderSubmitSection = () => (
    <div className="space-y-3">
      <div className="flex gap-4">
        <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
          <input type="checkbox" checked={requiresApp} onChange={(e) => setRequiresApp(e.target.checked)} className="rounded border-slate-300 text-indigo-500 focus:ring-indigo-300" data-testid="requires-app-check" />
          Requires app
        </label>
        <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
          <input type="checkbox" checked={requiresPurchase} onChange={(e) => setRequiresPurchase(e.target.checked)} className="rounded border-slate-300 text-indigo-500 focus:ring-indigo-300" data-testid="requires-purchase-check" />
          Requires purchase
        </label>
      </div>

      <div>
        <label className="text-xs font-semibold text-slate-600 block mb-1">Website (optional)</label>
        <input type="url" value={website} onChange={(e) => setWebsite(e.target.value)} placeholder="https://..." className="w-full text-sm border border-slate-200 rounded-xl px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-200" data-testid="website-input" />
      </div>

      <div>
        <label className="text-xs font-semibold text-slate-600 block mb-1">Additional notes (optional)</label>
        <textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Anything else we should know?" rows={2} className="w-full text-sm border border-slate-200 rounded-xl px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-200 resize-none" data-testid="notes-input" />
      </div>

      <button
        onClick={handleSubmit}
        disabled={submitting || !canSubmit}
        className="w-full py-3 text-sm font-semibold rounded-xl bg-slate-800 text-white hover:bg-slate-700 disabled:opacity-40 flex items-center justify-center gap-2"
        data-testid="submit-btn"
      >
        {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        {submitting ? "Submitting..." : "Submit for Review"}
      </button>
    </div>
  );

  /* ═══════ RENDER ═══════ */
  return (
    <div className="min-h-screen pb-24" style={{ backgroundColor: "var(--theme-page-bg, #ffffff)" }} data-testid="submit-offer-page">
      {/* Header */}
      <div className="sticky top-0 z-30 bg-white/90 backdrop-blur-md border-b border-slate-100 px-4 py-3 flex items-center gap-3">
        <button
          onClick={() => {
            if (mode === "new") { setMode("search"); return; }
            if (selectedMatch) { deselectMatch(); return; }
            navigate(-1);
          }}
          className="p-1.5 hover:bg-slate-100 rounded-lg"
          data-testid="submit-back-btn"
        >
          <ArrowLeft className="w-5 h-5 text-slate-600" />
        </button>
        <div className="flex-1">
          <h1 className="text-base font-bold text-slate-800">
            {mode === "new" ? "New Business" : "Submit a Deal"}
          </h1>
          {mode === "search" && !selectedMatch && (
            <p className="text-[10px] text-slate-400">Search for the business first</p>
          )}
          {mode === "search" && selectedMatch && (
            <p className="text-[10px] text-slate-400">Fill in the offer details</p>
          )}
          {mode === "new" && (
            <p className="text-[10px] text-slate-400">Tell us about this business</p>
          )}
        </div>
        {gpsLoading ? (
          <Loader2 className="w-4 h-4 animate-spin text-slate-400" />
        ) : gpsCoords ? (
          <div className="flex items-center gap-1 text-[10px] text-teal-600" data-testid="gps-active">
            <LocateFixed className="w-3.5 h-3.5" /> GPS
          </div>
        ) : null}
      </div>

      <div className="px-4 pt-4 space-y-4">
        {/* ══════════════════════════════════════════ */}
        {/*  SEARCH MODE                              */}
        {/* ══════════════════════════════════════════ */}
        {mode === "search" && (
          <>
            {/* Search bar — always visible */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => { setSearchQuery(e.target.value); if (selectedMatch) deselectMatch(); }}
                placeholder="Search for a business..."
                className={`w-full pl-9 pr-4 py-3 text-sm border rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-200 ${selectedMatch ? "border-emerald-300 bg-emerald-50/50" : "border-slate-200"}`}
                autoFocus={!selectedMatch}
                data-testid="business-name-input"
              />
              {checking && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-slate-400" />}
            </div>

            <AnimatePresence mode="wait">
              {/* ── No selection yet: show match results ── */}
              {!selectedMatch && (
                <motion.div key="results" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="space-y-3">
                  {searchQuery.length >= 2 && !checking && matches.length > 0 && (
                    <>
                      <div className="flex items-center gap-1.5 px-1">
                        <AlertTriangle className="w-3.5 h-3.5 text-amber-500" />
                        <span className="text-xs font-semibold text-amber-700">Select if you see the business</span>
                      </div>
                      <div className="space-y-2">
                        {matches.slice(0, 5).map((m) => (
                          <MatchCard key={m.business_id} match={m} selected={null} onSelect={handleSelectMatch} />
                        ))}
                      </div>
                      <button
                        onClick={goToNewMode}
                        className="w-full py-3 text-sm font-semibold rounded-xl border-2 border-dashed border-slate-200 text-slate-500 hover:border-slate-300 hover:bg-slate-50 flex items-center justify-center gap-2"
                        data-testid="none-of-these-btn"
                      >
                        None of these — it's a new business
                      </button>
                    </>
                  )}

                  {searchQuery.length >= 2 && !checking && matches.length === 0 && (
                    <>
                      <div className="flex items-center gap-2 bg-emerald-50 rounded-xl p-3 border border-emerald-200">
                        <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                        <span className="text-xs font-semibold text-emerald-700">No matches found — looks like a new one!</span>
                      </div>
                      <button
                        onClick={goToNewMode}
                        className="w-full py-3 text-sm font-semibold rounded-xl bg-slate-800 text-white hover:bg-slate-700 flex items-center justify-center gap-2"
                        data-testid="add-new-btn"
                      >
                        Add "{searchQuery}" as new business <ChevronRight className="w-4 h-4" />
                      </button>
                    </>
                  )}

                  {searchQuery.length < 2 && (
                    <div className="text-center py-8 text-slate-300">
                      <Search className="w-10 h-10 mx-auto mb-2" />
                      <p className="text-xs text-slate-400" data-testid="submit-empty-prompt">{getText("submit_business")}</p>
                    </div>
                  )}
                </motion.div>
              )}

              {/* ── Match selected: show details form inline ── */}
              {selectedMatch && (
                <motion.div key="details" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
                  {/* Selected business card with deselect */}
                  <div className="flex items-center gap-2.5 bg-emerald-50 rounded-xl p-3 border border-emerald-200">
                    {selectedMatch.logo_url && <img src={selectedMatch.logo_url} alt="" className="w-9 h-9 rounded-lg object-contain bg-white border border-slate-100" />}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-emerald-800">{selectedMatch.name}</p>
                      <p className="text-[10px] text-emerald-600">{selectedMatch.category}</p>
                    </div>
                    <button onClick={deselectMatch} className="p-1 hover:bg-emerald-100 rounded-lg" data-testid="deselect-match-btn">
                      <X className="w-4 h-4 text-emerald-400" />
                    </button>
                  </div>

                  {/* Location verification */}
                  {loadingLocations ? (
                    <div className="flex items-center gap-2 p-3 bg-slate-50 rounded-xl border border-slate-100">
                      <Loader2 className="w-4 h-4 animate-spin text-slate-400" />
                      <span className="text-xs text-slate-400">Finding nearby locations...</span>
                    </div>
                  ) : nearbyLocations.length > 0 ? (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 px-1">
                        <Navigation className="w-3.5 h-3.5 text-teal-500" />
                        <span className="text-xs font-semibold text-teal-700">
                          Which location? ({nearbyCount > 0 ? `${nearbyCount} near you` : `${nearbyLocations.length} found`})
                        </span>
                      </div>
                      <div className="space-y-1.5 max-h-[30vh] overflow-y-auto">
                        {(showAllLocations ? nearbyLocations : nearbyLocations.slice(0, 4)).map((loc) => (
                          <LocationCard key={loc.location_id} loc={loc} selected={selectedLocation} onSelect={(l) => { setSelectedLocation(l); setLocation(l.address); }} />
                        ))}
                      </div>
                      {nearbyLocations.length > 4 && !showAllLocations && (
                        <button onClick={() => setShowAllLocations(true)} className="w-full text-xs text-teal-600 font-medium py-1.5 hover:underline" data-testid="show-all-locations-btn">
                          Show all {nearbyLocations.length} locations
                        </button>
                      )}
                      {!selectedLocation && (
                        <p className="text-[10px] text-slate-400 px-1">Tap a location, or enter the address manually below</p>
                      )}
                    </div>
                  ) : null}

                  {/* Divider */}
                  <div className="border-t border-slate-100 pt-3">
                    <p className="text-xs font-bold text-slate-700 mb-3">Offer Details</p>
                  </div>

                  {renderOfferSection()}
                  {renderLocationField()}
                  {renderSubmitSection()}
                </motion.div>
              )}
            </AnimatePresence>
          </>
        )}

        {/* ══════════════════════════════════════════ */}
        {/*  NEW BUSINESS MODE                        */}
        {/* ══════════════════════════════════════════ */}
        {mode === "new" && (
          <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="space-y-4">
            <div className="bg-sky-50 rounded-xl p-3 border border-sky-200">
              <p className="text-xs font-semibold text-sky-800">Adding a new business</p>
              <p className="text-[10px] text-sky-600">Tell us about their birthday deal!</p>
            </div>

            <div>
              <label className="text-xs font-semibold text-slate-600 block mb-1">Business Name</label>
              <input
                type="text"
                value={newBusinessName}
                onChange={(e) => setNewBusinessName(e.target.value)}
                placeholder="e.g., Joe's Pizza"
                className="w-full text-sm border border-slate-200 rounded-xl px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-200"
                autoFocus
                data-testid="new-business-name-input"
              />
            </div>

            <div>
              <label className="text-xs font-semibold text-slate-600 block mb-1">Category</label>
              <select value={category} onChange={(e) => setCategory(e.target.value)} className="w-full text-sm border border-slate-200 rounded-xl px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-200 bg-white" data-testid="category-select">
                <option value="">Select a category</option>
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>

            {renderOfferSection()}
            {renderLocationField()}
            {renderSubmitSection()}
          </motion.div>
        )}

        {/* ══════════════════════════════════════════ */}
        {/*  SUCCESS                                  */}
        {/* ══════════════════════════════════════════ */}
        {mode === "success" && (
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="text-center py-12 space-y-4">
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto">
              <CheckCircle className="w-8 h-8 text-emerald-600" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-800" data-testid="success-heading">Submitted!</h2>
              <p className="text-sm text-slate-500 mt-1">
                {selectedMatch
                  ? `Your update for ${selectedMatch.name} is pending review.`
                  : `${newBusinessName || searchQuery} has been submitted for review.`}
              </p>
              {selectedLocation && (
                <p className="text-xs text-teal-600 mt-1 flex items-center justify-center gap-1">
                  <MapPin className="w-3 h-3" /> Location verified: {selectedLocation.address}
                </p>
              )}
              <p className="text-xs text-slate-400 mt-1">You earned 30 points for this submission!</p>
            </div>
            <div className="flex gap-3 justify-center pt-2">
              <button onClick={resetForm} className="px-4 py-2 text-sm font-semibold bg-slate-100 text-slate-700 rounded-xl hover:bg-slate-200" data-testid="submit-another-btn">
                Submit Another
              </button>
              <button onClick={() => navigate("/")} className="px-4 py-2 text-sm font-semibold bg-slate-800 text-white rounded-xl hover:bg-slate-700" data-testid="go-home-btn">
                Back to Home
              </button>
            </div>
          </motion.div>
        )}
      </div>

      <BottomNav />
    </div>
  );
}
